import moment from 'moment';

import {
  HISTORY_EVENT_TYPE_ID,
  LICENSE_CONSTANTS,
} from '@/constants';

import {
  credit_repository,
  history_repository,
} from '@/repositories';

const state = () => ({
  credit_sequence_users: [],
  organization_campaign_credits: null,
  organization_campaign_credits_is_renewed: false,
  organization_consultation_credits: null,
  organization_consultation_credits_is_renewed: false,
  organization_consultation_credits_renewal: null,
  organization_connector_credits: null,
  user_campaign_credits: null,
  user_campaign_credits_is_renewed: false,
  user_campaign_credits_renewal: null,
  user_consultation_credits: null,
  user_consultation_credits_renewal_info: null,
  user_email_rental_credits: null,
  user_email_rental_credits_is_renewed: false,
  user_export_credits: null,
  user_export_credits_is_renewed: false,
  users_campaign_credits: null,
  users_email_rental_credits: null,
  users_export_credits: null,
});

const getters = {
  creditSequenceUsers: (state) => state.credit_sequence_users,
  organizationCampaignCredits: (state) => state.organization_campaign_credits,
  organizationCampaignCreditsIsRenewed: (state) => state.organization_campaign_credits_is_renewed,
  organizationConsultationCredits: (state) => state.organization_consultation_credits,
  organizationConsultationCreditsIsRenewed: (state) => state
    .organization_consultation_credits_is_renewed,
  organizationConsultationCreditsRenewal: (state) => state
    .organization_consultation_credits_renewal,
  organizationConnectorCredits: (state) => state.organization_connector_credits,
  effectiveConsultationCredits: (state, getters, rootState) => {
    const user_data = rootState.user?.user_data;
    const license_id = user_data?.organization_user_license_list?.at(0)?.license?.id;
    const uses_organization_consultation_credits = [
      LICENSE_CONSTANTS.SMART_DATA.ID,
      LICENSE_CONSTANTS.SALES_AND_MARKETING_V2.ID,
    ].includes(license_id);

    if (uses_organization_consultation_credits) {
      return state.organization_consultation_credits ?? 0;
    }

    return state.user_consultation_credits ?? 0;
  },
  userConsultationCredits: (state) => state.user_consultation_credits,
  userConsultationCreditsRenewalInfo: (state) => state.user_consultation_credits_renewal_info,
  userCampaignCredits: (state) => state.user_campaign_credits,
  userCampaignCreditsIsRenewed: (state) => state.user_campaign_credits_is_renewed,
  userCampaignCreditsRenewal: (state) => state.user_campaign_credits_renewal,
  userEmailRentalCredits: (state) => state.user_email_rental_credits,
  userEmailRentalCreditsIsRenewed: (state) => state.user_email_rental_credits_is_renewed,
  userExportCredits: (state) => state.user_export_credits,
  userExportCreditsIsRenewed: (state) => state.user_export_credits_is_renewed,
  usersCampaignCredits: (state) => state.users_campaign_credits,
  usersEmailRentalCredits: (state) => state.users_email_rental_credits,
  usersExportCredits: (state) => state.users_export_credits,
};

const actions = {
  getSequenceCreditsForUser({ state }, user_id) {
    const credit = state.credit_sequence_users
      .find((credit) => credit.user_id === user_id);

    return (credit === undefined)
      ? 0
      : credit.amount;
  },
  async getCreditSequenceUsers({ commit }, { organization_id }) {
    const credits = await credit_repository.getCreditSequenceUsers({
      organization_id,
      subscription_last_date: moment().toISOString(),
    });

    commit('set_credit_sequence_users', credits.user_list);
  },
  async getOrganizationCampaignCredits({ commit }, { organization_id }) {
    const organization_campaign_credits = await credit_repository
      .getOrganizationCampaignCredits({
        organization_id,
      });

    commit('set_organization_campaign_credits', organization_campaign_credits?.amount);
    commit('set_organization_campaign_credits_is_renewed', organization_campaign_credits?.is_renewed);

    return organization_campaign_credits;
  },
  async getOrganizationConnectorCredits({ commit }, { organization_id }) {
    try {
      const organization_connector_credits = await credit_repository
        .getOrganizationConnectorCredits({
          organization_id,
        });

      commit('set_organization_connector_credits', organization_connector_credits?.amount);

      return organization_connector_credits;
    } catch (error) {
      commit('set_organization_connector_credits', 0);

      return null;
    }
  },
  async getOrganizationConsultationCredits({ commit }, { organization_id }) {
    const organization_consultation_credits = await credit_repository
      .getOrganizationConsultationCredits({
        organization_id,
      });

    commit('set_organization_consultation_credits', organization_consultation_credits?.amount);
    commit('set_organization_consultation_credits_is_renewed', organization_consultation_credits?.is_renewed);
    commit('set_organization_consultation_credits_renewal', organization_consultation_credits?.renewal_amount);

    return organization_consultation_credits;
  },
  async getUserConsultationCredits({ commit, dispatch, rootState }, { organization_id, user_id }) {
    const user_data = rootState.user?.user_data;
    const license_id = user_data?.organization_user_license_list?.at(0)?.license?.id;
    const uses_organization_consultation_credits = [
      LICENSE_CONSTANTS.SMART_DATA.ID,
      LICENSE_CONSTANTS.SALES_AND_MARKETING_V2.ID,
    ].includes(license_id);

    try {
      if (uses_organization_consultation_credits) {
        const organization_consultation_credits = await dispatch('getOrganizationConsultationCredits', {
          organization_id,
        });

        commit('set_user_consultation_credits_renewal_info', {
          is_renewed: organization_consultation_credits?.is_renewed,
          renewal_amount: organization_consultation_credits?.renewal_amount,
          renewal_period: organization_consultation_credits?.renewal_period,
        });

        return organization_consultation_credits;
      }

      const user_consultation_credits = await credit_repository
        .getUserConsultationCredits({
          organization_id,
          user_id,
        });

      commit('set_user_consultation_credits', user_consultation_credits?.amount);
      commit('set_user_consultation_credits_renewal_info', {
        is_renewed: user_consultation_credits?.is_renewed,
        renewal_amount: user_consultation_credits?.renewal_amount,
        renewal_period: user_consultation_credits?.renewal_period,
      });

      return user_consultation_credits;
    } catch (error) {
      commit('set_organization_consultation_credits', 0);
      commit('set_user_consultation_credits', 0);
      commit('set_user_consultation_credits_renewal_info', false);

      return null;
    }
  },
  async getUserCampaignCredits({ commit }, { organization_id, user_id }) {
    try {
      let user_campaign_credits = null;

      if (!user_id) {
        const organization_campaign_credits = await credit_repository
          .getCampaignCredits({
            organization_id,
          });

        user_campaign_credits = organization_campaign_credits;
      } else {
        user_campaign_credits = await credit_repository
          .getUserCampaignCredits({
            organization_id,
            user_id,
          });
      }

      commit('set_user_campaign_credits', user_campaign_credits?.amount);
      commit('set_user_campaign_credits_is_renewed', user_campaign_credits?.is_renewed);
      commit('set_user_campaign_credits_renewal', user_campaign_credits?.renewal_amount);

      return user_campaign_credits;
    } catch (error) {
      commit('set_user_campaign_credits', 0);
      commit('set_user_campaign_credits_is_renewed', false);

      return null;
    }
  },
  async getUserEmailRentalCredits({ commit }, { organization_id, user_id }) {
    try {
      const user_email_rental_credits = await credit_repository
        .getUserEmailRentalCredits({
          organization_id,
          user_id,
        });

      commit('set_user_email_rental_credits', user_email_rental_credits?.amount);
      commit('set_user_email_rental_credits_is_renewed', user_email_rental_credits?.is_renewed);

      return user_email_rental_credits;
    } catch (error) {
      commit('set_user_email_rental_credits', 0);
      commit('set_user_email_rental_credits_is_renewed', false);

      return null;
    }
  },
  async getUserExportCredits({ commit }, { organization_id, user_id }) {
    try {
      const user_export_credits = await credit_repository
        .getUserExportCredits({
          organization_id,
          user_id,
        });

      commit('set_user_export_credits', user_export_credits?.amount);
      commit('set_user_export_credits_is_renewed', user_export_credits?.is_renewed);

      return user_export_credits;
    } catch (error) {
      commit('set_user_export_credits', 0);
      commit('set_user_export_credits_is_renewed', false);

      return null;
    }
  },
  async getUsersCampaignCredits({ commit }, { organization_id, params }) {
    const response = await credit_repository
      .getUsersCampaignCredits({
        organization_id,
        params,
      });

    commit('set_users_campaign_credits', response);

    return response;
  },
  async getUsersEmailRentalCredits({ commit }, { organization_id, params }) {
    const response = await credit_repository
      .getUsersEmailRentalCredits({
        organization_id,
        params,
      });

    commit('set_users_email_rental_credits', response);

    return response;
  },
  async getUsersExportCredits({ commit }, { organization_id, params }) {
    const response = await credit_repository
      .getUsersExportCredits({
        organization_id,
        params,
      });

    commit('set_users_export_credits', response);

    return response;
  },
  async updateCampaignCredits({ dispatch, rootState }, input) {
    const {
      create_credit,
      credit_count_new,
      credit_count_old,
      organization_id,
      user_id,
    } = input;

    const credit_count = credit_count_new - credit_count_old;

    const data = {
      user_id_list: [user_id],
      total_amount_for_user_id_list: credit_count,
      comment: `${credit_count} credits added`,
    };

    if (create_credit) {
      await credit_repository.createCampaignCredits(
        organization_id,
        user_id,
        {
          initial_amount: 0,
          renewal_amount: 0,
        }
      );
    }

    const response = await credit_repository.updateCampaignCredits(organization_id, data);

    history_repository.createHistory({
      author_user_id: user_id,
      creation_date: null,
      history_event_id: HISTORY_EVENT_TYPE_ID.USER_CREDIT,
      is_shared: true,
      params: JSON.stringify({
        credit: {
          new_amount: credit_count_new,
          old_amount: credit_count_old,
          type: 'sequence',
        },
      }),
      subject_account_id: null,
      subject_contact_id: null,
      subject_lead_id: null,
      subject_organization_id: null,
      subject_user_id: user_id,
    });

    if (rootState.user.user_data.id === user_id) {
      dispatch(
        'getUserCampaignCredits',
        {
          organization_id: rootState.user.organization.id,
          user_id: rootState.user.user_data.id,
        }
      );
    }

    dispatch(
      'getUsersCampaignCredits',
      {
        organization_id,
        params: {
          subscription_last_date: rootState.user.organization.subscription.start_date,
        },
      }
    );

    return response;
  },
  async updateEmailRentalredits({ dispatch, rootState }, input) {
    const {
      create_credit,
      credit_count_new,
      credit_count_old,
      organization_id,
      user_id,
    } = input;

    const credit_count = credit_count_new - credit_count_old;

    const data = {
      user_id_list: [user_id],
      total_amount_for_user_id_list: credit_count,
      comment: `${credit_count} credits added`,
    };

    if (create_credit) {
      await credit_repository.createEmailRentalredits(
        organization_id,
        user_id,
        {
          initial_amount: 0,
          renewal_amount: 0,
        }
      );
    }

    const response = await credit_repository.updateEmailRentalredits(organization_id, data);

    history_repository.createHistory({
      author_user_id: user_id,
      creation_date: null,
      history_event_id: HISTORY_EVENT_TYPE_ID.USER_CREDIT,
      is_shared: true,
      params: JSON.stringify({
        credit: {
          new_amount: credit_count_new,
          old_amount: credit_count_old,
          type: 'email-rental',
        },
      }),
      subject_account_id: null,
      subject_contact_id: null,
      subject_lead_id: null,
      subject_organization_id: null,
      subject_user_id: user_id,
    });

    if (rootState.user.user_data.id === user_id) {
      dispatch(
        'getUserEmailRentalCredits',
        {
          organization_id: rootState.user.organization.id,
          user_id: rootState.user.user_data.id,
        }
      );
    }

    dispatch(
      'getUsersEmailRentalCredits',
      {
        organization_id,
        params: {
          subscription_last_date: rootState.user.organization.subscription.start_date,
        },
      }
    );

    return response;
  },
  async updateExportCredits({ dispatch, rootState }, input) {
    const {
      create_credit,
      credit_count_new,
      credit_count_old,
      organization_id,
      user_id,
    } = input;

    const credit_count = credit_count_new - credit_count_old;

    const data = {
      user_id_list: [user_id],
      total_amount_for_user_id_list: credit_count,
      comment: `${credit_count} credits added`,
    };

    if (create_credit) {
      await credit_repository.createExportCredits(
        organization_id,
        user_id,
        {
          initial_amount: 0,
          renewal_amount: 0,
        }
      );
    }

    const response = await credit_repository.updateExportCredits(organization_id, data);

    history_repository.createHistory({
      author_user_id: user_id,
      creation_date: null,
      history_event_id: HISTORY_EVENT_TYPE_ID.USER_CREDIT,
      is_shared: true,
      params: JSON.stringify({
        credit: {
          new_amount: credit_count_new,
          old_amount: credit_count_old,
          type: 'export',
        },
      }),
      subject_account_id: null,
      subject_contact_id: null,
      subject_lead_id: null,
      subject_organization_id: null,
      subject_user_id: user_id,
    });

    if (rootState.user.user_data.id === user_id) {
      dispatch(
        'getUserExportCredits',
        {
          organization_id: rootState.user.organization.id,
          user_id: rootState.user.user_data.id,
        }
      );
    }

    dispatch(
      'getUsersExportCredits',
      {
        organization_id,
        params: {
          subscription_last_date: rootState.user.organization.subscription.start_date,
        },
      }
    );

    return response;
  },
  async withdrawContactConsultationCredit(_, input) {
    const response = await credit_repository.withdrawContactConsultationCredit(input);

    return response;
  },
};

const mutations = {
  set_credit_sequence_users(state, credit_sequence_users) {
    state.credit_sequence_users = credit_sequence_users;
  },
  set_organization_campaign_credits(state, organization_campaign_credits) {
    state.organization_campaign_credits = organization_campaign_credits;
  },
  set_organization_campaign_credits_is_renewed(state, organization_campaign_credits_is_renewed) {
    state.organization_campaign_credits_is_renewed = organization_campaign_credits_is_renewed;
  },
  set_organization_consultation_credits(state, organization_consultation_credits) {
    state.organization_consultation_credits = organization_consultation_credits;
  },
  set_organization_consultation_credits_is_renewed(
    state,
    organization_consultation_credits_is_renewed
  ) {
    // eslint-disable-next-line vue/max-len
    state.organization_consultation_credits_is_renewed = organization_consultation_credits_is_renewed;
  },
  set_organization_consultation_credits_renewal(state, organization_consultation_credits_renewal) {
    state.organization_consultation_credits_renewal = organization_consultation_credits_renewal;
  },
  set_organization_connector_credits(state, organization_connector_credits) {
    state.organization_connector_credits = organization_connector_credits;
  },
  set_user_consultation_credits(state, user_consultation_credits) {
    state.user_consultation_credits = user_consultation_credits;
  },
  set_user_consultation_credits_renewal_info(state, user_consultation_credits_renewal_info) {
    state.user_consultation_credits_renewal_info = user_consultation_credits_renewal_info;
  },
  set_user_campaign_credits(state, user_campaign_credits) {
    state.user_campaign_credits = user_campaign_credits;
  },
  set_user_campaign_credits_is_renewed(state, user_campaign_credits_is_renewed) {
    state.user_campaign_credits_is_renewed = user_campaign_credits_is_renewed;
  },
  set_user_campaign_credits_renewal(state, user_campaign_credits_renewal) {
    state.user_campaign_credits_renewal = user_campaign_credits_renewal;
  },
  set_user_email_rental_credits(state, user_email_rental_credits) {
    state.user_email_rental_credits = user_email_rental_credits;
  },
  set_user_email_rental_credits_is_renewed(state, user_email_rental_credits_is_renewed) {
    state.user_email_rental_credits_is_renewed = user_email_rental_credits_is_renewed;
  },
  set_user_export_credits(state, user_export_credits) {
    state.user_export_credits = user_export_credits;
  },
  set_user_export_credits_is_renewed(state, user_export_credits_is_renewed) {
    state.user_export_credits_is_renewed = user_export_credits_is_renewed;
  },
  set_users_campaign_credits(state, users_campaign_credits) {
    state.users_campaign_credits = users_campaign_credits;
  },
  set_users_email_rental_credits(state, users_email_rental_credits) {
    state.users_email_rental_credits = users_email_rental_credits;
  },
  set_users_export_credits(state, users_export_credits) {
    state.users_export_credits = users_export_credits;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
